import React from 'react';
import PropTypes from 'prop-types';
import {
  FDStack,
  FDLabelText,
  FDPrimaryActionButton,
  FDContentContainer,
  FDTitleText,
  FDBox,
  FDAccordionGroup,
  FDAccordionTitle,
  FDAccordionDetails,
  FDAccordion,
} from '@facet-wealth/design';
import MetaFormInline from 'Components/UI/Meta/MetaForm/Components/MetaFormInline';
import MetaControlField from 'Components/UI/Meta/MetaForm/Components/MetaControlField';
import { usePageContentContext } from 'Views/Page/Contexts/PageContentContext';
import { buildFacetDesignOverride, overrideReadOnly } from 'Packages/FacetRest/FacetDesignOverride';
import useMetaLayoutContext from 'Components/UI/Meta/MetaForm/Components/MetaLayoutContext';

const metaOverrideSet = buildFacetDesignOverride()
  .nameOverride(['title'], {
    _component: () => FDLabelText,
  }, overrideReadOnly);

function PayrollDeductionsColumn({ children }) {
  return (
    <FDStack direction="column" spacing={2} flexGrow={1}>
      {children}
    </FDStack>
  );
}

PayrollDeductionsColumn.defaultProps = {};

PayrollDeductionsColumn.propTypes = {
  children: PropTypes.node.isRequired,
};

const PayrollDeductionsInnerSection = ({
  attrNames,
}) => {
  return (
    <PayrollDeductionsColumn>
      {attrNames.map((attrName) => (<MetaControlField attr={attrName} />))}
    </PayrollDeductionsColumn>
  );
};

PayrollDeductionsInnerSection.defaultProps = {};
PayrollDeductionsInnerSection.propTypes = {
  attrNames: PropTypes.arrayOf(PropTypes.string).isRequired,
};

const pretaxContributions = {
  title: 'Pre-Tax Contributions',
  attrNames: [
    'pretaxContributionEsp',
    'pretaxContributionHsa',
    'pretaxContributionFsa',
    'pretaxContributionOther',
  ],
};

const postTaxContributions = {
  title: 'Post-Tax Contributions',
  attrNames: [
    'postTaxContributionEsp',
    'postTaxContributionOther',
  ],
};

const medicalDeductions = {
  title: 'Medical Deductions',
  attrNames: [
    'medicalContribution',
    'dentalContribution',
    'visionContribution',
  ],
};

const innerSections = [
  pretaxContributions,
  postTaxContributions,
  medicalDeductions,
];

function PayrollDeductionsJobLayout() {
  return (
    <FDStack direction="column" alignItems="stretch" spacing={2}>
      <FDStack spacing={2} flexGrow={1}>
        <PayrollDeductionsColumn>
          <MetaControlField attr="title" />
          <FDAccordionGroup>
            {innerSections.map((section) => (
              <FDAccordion expansionID={section.title}>
                <FDAccordionTitle>
                  <FDBox flexGrow={1}>
                    <FDTitleText value={section.title} />
                  </FDBox>
                </FDAccordionTitle>
                <FDAccordionDetails>
                  <PayrollDeductionsInnerSection attrNames={section.attrNames} />
                </FDAccordionDetails>
              </FDAccordion>
            ))}
          </FDAccordionGroup>
        </PayrollDeductionsColumn>
      </FDStack>
    </FDStack>
  );
}

function PayrollDeductionsLayout() {
  const { resource, saving, onSubmit } = useMetaLayoutContext();

  const primaryJobs = resource.attr('primaryJobs').resources().list();
  const spouseJobs = resource.attr('spouseJobs').resources().list();

  const payrollDeductionsResources = [
    ...primaryJobs,
    ...spouseJobs,
  ];
  return (
    <>
      {payrollDeductionsResources.map((res) => (
        <FDBox paddingY={2} key={`payroll-deductions-${res.id()}`}>
          <MetaFormInline
            padding="none"
            noIndividualSubmitButtons
            layout={PayrollDeductionsJobLayout}
            resource={res}
            metaOverrideSet={metaOverrideSet}
          />
        </FDBox>
      ))}
      {resource.meta().canSubmit()
        && <FDPrimaryActionButton
          test="facet-plan-save-available-resources-payroll-deductions"
          label="Save"
          loading={saving}
          onClick={onSubmit}
        />
       }
    </>
  );
}

const PayrollDeductionsSection = ({
  resource: availableResourcesResource,
}) => {
  const { refreshPageContentCollection } = usePageContentContext();
  const onSubmit = () => {
    refreshPageContentCollection();
  };

  if (!availableResourcesResource) {
    return null;
  }

  const payrollDeductionsResource = availableResourcesResource
    .attr('payrollDeductions').resources().head();

  if (!payrollDeductionsResource) {
    return null;
  }

  return (
    <FDContentContainer maxWidth="sm">
      <MetaFormInline
        padding="none"
        noIndividualSubmitButtons
        layout={PayrollDeductionsLayout}
        resource={payrollDeductionsResource}
        metaOverrideSet={metaOverrideSet}
        onSubmit={onSubmit}
      />
    </FDContentContainer>
  );
};

PayrollDeductionsSection.defaultProps = {};

PayrollDeductionsSection.propTypes = {
  resource: PropTypes.object.isRequired,
};

export default PayrollDeductionsSection;
